import type { Appointment } from "@shyne/shared/api";
import { bookingRefusalMessage, type Booking } from "@/lib/appointment-booking";
import type { PlannedAppointmentRow } from "./bulk-upload-plan";

export interface BulkUploadResult {
  created: number;
  failed: number;
  errors: string[];
}

export type BookRow = (booking: Booking) => Promise<Appointment>;

class MissingUnit extends Error {}

function bookingOf(row: PlannedAppointmentRow): Booking {
  if (!row.unitId) throw new MissingUnit(`Unit "${row.unitNumber}" does not exist in property "${row.propertyName}"`);
  return {
    property: row.propertyId,
    unit: row.unitId,
    service: row.serviceId,
    vendor: row.vendorOrgId,
    date: row.appointmentDate,
    ...(row.workNotes ? { workOrderNotes: row.workNotes } : {}),
    bulkUploadRow: row.rowNumber,
  };
}

const rowFailure = (error: unknown) => (error instanceof MissingUnit ? error.message : bookingRefusalMessage(error));

export async function importPlannedRows(
  planned: PlannedAppointmentRow[],
  book: BookRow,
  onProgress?: (completed: number, total: number) => void
): Promise<BulkUploadResult> {
  const rows = [...planned].sort((a, b) => a.rowNumber - b.rowNumber);
  const errors: string[] = [];
  let created = 0;

  for (const [index, row] of rows.entries()) {
    try {
      await book(bookingOf(row));
      created++;
    } catch (error) {
      errors.push(`Row ${row.rowNumber}: ${rowFailure(error)}`);
    } finally {
      onProgress?.(index + 1, rows.length);
    }
  }

  return { created, failed: rows.length - created, errors };
}
